import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Input from '../components/blog/shared/Input';
import Button from '../components/blog/ui/Button';
import BlogPostCard from '../components/blog/BlogPostCard';
import FeaturedBlogPost from '../components/blog/FeaturedBlogPost';
import BlogNewsletterBanner from '../components/blog/BlogNewsletterBanner';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchBlogPosts } from '../features/blog/thunks/blog.thunk';
import { BLOG_TABS } from '../utils/constants/blog/blogData';
import missionImage from '../assets/brand/mission.jpg';
import blogImage1 from '../assets/brand/9192b75128db15551a817c34ed509c897729a677.png';
import blogImage2 from '../assets/brand/e810b0093a8e6d120aff7385e42eef67d2bced9a.png';
import blogImage3 from '../assets/brand/8d1c5b8fa553e7886a350fc28d187f1653173c19.jpg';
import blogImage4 from '../assets/brand/93e2b48b30868ae283e24cf37284cad47fc40a29.jpg';
import blogImage5 from '../assets/brand/8d1c5b8fa553e7886a350fc28d187f1653173c19 (1).jpg';
import blogImage6 from '../assets/brand/1ed4b2ed25d73fd15cfb8fa54e2514d23e3ba07c.jpg';

const BLOG_LIST_IMAGES = [blogImage1, blogImage2, blogImage3, blogImage4, blogImage5, blogImage6];


const stripHtml = (html: string) => {
  return html.replace(/<[^>]*>?/gm, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
};

const Blog: React.FC = () => {
  const dispatch = useAppDispatch();
  const { posts, isLoading, error } = useAppSelector((state) => state.blog);
  const [activeTab, setActiveTab] = useState('All');
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(6);

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(fetchBlogPosts(20));
  }, [dispatch]);

  const mappedPosts = useMemo(() => {
    return posts.map((post, index) => {
      const fallbackImage = index === 0 ? missionImage : BLOG_LIST_IMAGES[(index - 1) % BLOG_LIST_IMAGES.length];
      return {
        id: String(post.id),
        slug: post.slug,
        category: post.categoryNames?.[0] || 'Uncategorized',
        tag: post.categoryNames?.[0] || 'Uncategorized',
        title: post.title,
        description: stripHtml(post.excerptHtml || '').slice(0, 120) + '...',
        author: post.authorName || 'Baoafrik Team',
        authorAvatar: post.authorAvatar || '',
        date: new Date(post.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).toUpperCase(),
        readTime: 'Read in 5 min',
        image: post.featuredMediaUrl || fallbackImage,
      };
    });
  }, [posts]);

  const filteredPosts = useMemo(() => {
    const term = search.trim().toLowerCase();
    return mappedPosts.filter((post) => {
      const matchesTab = activeTab === 'All' || post.category === activeTab;
      const matchesSearch =
        !term ||
        post.title.toLowerCase().includes(term) ||
        post.description.toLowerCase().includes(term);
      return matchesTab && matchesSearch;
    });
  }, [activeTab, search, mappedPosts]);

  const featuredPost = filteredPosts[0] || (mappedPosts[0] as any);
  const listPosts = filteredPosts.slice(1, visibleCount + 1);
  const canLoadMore = filteredPosts.length - 1 > listPosts.length;

  return (
    <div className="w-full bg-white select-none">
      <div className="layout-container lg:pt-0 pb-16">

        {/* Hero Section */}
        <header className="text-center mt-8 mb-10 px-4 flex flex-col items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col items-center"
          >
            <span 
              className="px-3 py-1 border rounded-full text-[10px] font-normal text-[#919191] mb-4 border-[#B8B8B8] font-description"
            >
               Our Blogs
            </span>
            <h1
              className="text-[32px] sm:text-[40px] lg:text-[46px] tracking-[-0.02em] leading-[1.08] font-title font-semibold text-[#212121]"
            >
              Insights and inspiration, <br className="hidden sm:block" /> explore our blogs
            </h1>
            <p className="mt-3 max-w-[560px] mx-auto text-[13px] md:text-[14px] leading-[1.55] text-[#919191] font-description font-light">
              A curated selection for visionaries: thoughts on e-commerce, industry, the future of work, and subtle management leadership.
            </p>
          </motion.div>

          {/* Search Bar */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-8 flex justify-center w-full"
          >
            <div className="relative w-full max-w-[400px] group">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search for a blog"
                className="w-full px-4 py-2.5 pl-10 focus:outline-none transition-all rounded-full bg-gray-50 border-gray-100 focus:bg-white focus:border-primary-light shadow-none border font-description text-[12px] text-gray-900"
              />
              <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary transition-colors" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
            </div>
          </motion.div>
        </header>

        {/* Category Tabs */}
        <div
          className="mt-8 border-b border-gray-100 w-screen relative left-1/2 -translate-x-1/2"
        >
          <div className="max-w-layout mx-auto px-4 sm:px-6 md:px-8 lg:px-0 flex items-center justify-start lg:justify-center gap-6 overflow-x-auto whitespace-nowrap [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {BLOG_TABS.map((tab) => {
              const active = activeTab === tab;
              return (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab);
                    setVisibleCount(6);
                  }}
                  className={`relative pb-3 px-1.5 transition-colors outline-none group font-title text-[12px] font-medium ${active ? 'text-primary' : 'text-gray-400 hover:text-gray-900'}`}
                >
                  <span className="relative z-10">{tab}</span>
                  {active && (
                    <motion.div 
                      layoutId="tab-underline"
                      className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-primary" 
                    />
                  )}
                  {!active && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-transparent group-hover:bg-gray-200 transition-colors rounded-full" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Blog Content Grid */}
        <main className="mt-8 lg:mt-12 min-h-[400px]">
          {isLoading && posts.length === 0 ? (
            <div className="py-20 text-center">
              <motion.div 
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="inline-block w-8 h-8 border-4 border-gray-100 rounded-full border-t-primary"
              />
              <p className="mt-4 text-[13px] text-gray-500 font-medium font-title">Discovering stories for you...</p>
            </div>
          ) : error ? (
            <div className="py-20 text-center">
              <div className="bg-red-50 text-red-600 px-5 py-3 rounded-xl inline-block">
                 <p className="font-title text-[13px] font-medium">Oops! We couldn't load the posts. Please try again.</p>
              </div>
              <div className="mt-4">
                <Button unstyled onClick={() => dispatch(fetchBlogPosts(20))} className="text-primary text-[13px] font-medium underline hover:text-primary-dark transition-colors">Retry Fetching</Button>
              </div>
            </div>
          ) : (
            <>
              {featuredPost && <FeaturedBlogPost post={featuredPost} />}

              <section className="mt-12 mb-12">
                <div className="flex items-center justify-between mb-6">
                   <h4 className="text-[16px] font-title font-medium text-gray-900">All blog posts</h4>
                </div>
                
                <motion.div 
                  layout
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10"
                >
                  <AnimatePresence mode="popLayout">
                    {listPosts.map((post) => (
                      <BlogPostCard key={post.id} post={post} />
                    ))}
                  </AnimatePresence>
                </motion.div>

                {listPosts.length === 0 && (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-16 bg-gray-50 rounded-[24px] mt-8"
                  >
                     <div className="text-2xl mb-3">🔍</div>
                     <p className="text-gray-500 text-[13px] font-description">We couldn't find any articles matching "{search}" in {activeTab}.</p>
                     <Button unstyled onClick={() => {setSearch(''); setActiveTab('All')}} className="mt-3 underline text-[13px] font-medium text-primary">Clear all filters</Button>
                  </motion.div>
                )}
              </section>

              {canLoadMore && (
                <div className="mt-6 mb-14 flex justify-center">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setVisibleCount((prev) => prev + 3)}
                    className="px-8 py-2.5 bg-white border border-gray-900 rounded-[12px] text-gray-900 font-medium text-[12px] hover:bg-gray-900 hover:text-white transition-all font-title"
                  >
                    Explore More Stories
                  </motion.button>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      <BlogNewsletterBanner
        title="Stay ahead of the curve"
        description="Receive the latest insights, market updates, and artisan stories directly in your inbox."
        titleSize="28px"
        mobileTitleLines={['Stay ahead of', 'the curve']}
      />
    </div>
  );
};

export default Blog;

